import React from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import CustomSnackbar from './utils/CustomSnackbar';

interface PostCommentFormProps {
  onSubmit?: (values: CommentFormValues) => Promise<void> | void;
}

interface CommentFormValues {
  name: string;
  email: string;
  comment: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must not exceed 50 characters')
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  comment: Yup.string()
    .min(10, 'Comment must be at least 10 characters')
    .max(1000, 'Comment must not exceed 1000 characters')
    .required('Comment is required'),
});

export default function PostCommentForm({ onSubmit }: PostCommentFormProps) {
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const handleSubmit = async (
    values: CommentFormValues,
    { setSubmitting, resetForm }: FormikHelpers<CommentFormValues>
  ) => {
    console.log('Form submitted with values:', values);
    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(values);
      }
      
      setSubmitSuccess(true);
      resetForm();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        name: '',
        email: '',
        comment: '',
      }}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, values, isSubmitting, handleSubmit: formikHandleSubmit }) => (
        <>
          <Form noValidate autoComplete="off" onSubmit={formikHandleSubmit}>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Post Comment
              </Typography>

              <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  fullWidth
                  label="Name"
                  name="name"
                  variant="outlined"
                  error={errors.name && touched.name}
                  helperText={errors.name && touched.name ? String(errors.name) : ''}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  variant="outlined"
                  error={!!errors.email && touched.email}
                  helperText={errors.email && touched.email ? String(errors.email) : ''}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  fullWidth
                  label="Comment"
                  name="comment"
                  multiline
                  rows={4}
                  variant="outlined"
                  error={!!errors.comment && touched.comment}
                  helperText={errors.comment && touched.comment ? String(errors.comment) : ''}
                  disabled={isSubmitting}
                  placeholder="Share your thoughts about this post..."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{ fontWeight: 600 }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Comment'}
                </Button>
              </Grid>
            </Grid>
          </Box>
          </Form>

          <CustomSnackbar
            open={submitSuccess}
            message="Thank you for your comment! It will be displayed after moderation."
            severity="success"
            onClose={() => setSubmitSuccess(false)}
          />
        </>
      )}
    </Formik>
  );
}